import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  Input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { type CustomFunction, OutputProperty } from '@blaze-shared/api-contracts-domain';
import {
  ButtonIconComponent,
  InputMaskDirective,
  PredefinedInputMaskOptions,
  TooltipDirective,
} from '@blaze-shared/shared-ui';

import {
  parseFunctionFormValue,
  parseFunctionToFormValue,
} from '../openai-connector-functions.helpers';
import {
  OpenAiCustomFunctionForm,
  OUTPUT_DATA_TYPE_OPTIONS,
} from '../openai-connector-functions.models';
import { OpenAIConnectorOutputParametersComponent } from '../openai-connector-output-parameters/openai-connector-output-parameters.component';

@UntilDestroy()
@Component({
  selector: 'blaze-openai-connector-function',
  templateUrl: './openai-connector-function.component.html',
  styleUrls: ['./openai-connector-function.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    ButtonIconComponent,
    OpenAIConnectorOutputParametersComponent,
    InputMaskDirective,
    TooltipDirective,
  ],
})
export class OpenAIConnectorFunctionComponent implements AfterViewInit {
  functionChange = output<CustomFunction>();
  isValid = output<boolean>();
  removeFunction = output<void>();

  @Input() function: CustomFunction;

  @ViewChild('functionNameInput') functionNameInput?: ElementRef<HTMLInputElement>;

  isFormValid = signal(false);
  isChildValid = signal(true);

  functionWrapped = signal(true);
  outputDataTypeOptions = OUTPUT_DATA_TYPE_OPTIONS;
  showAdvanced = false;
  temperatureMaskOptions: PredefinedInputMaskOptions = {
    numberFormat: {
      format: 'number',
      precision: 1,
      placeholder: '1.0',
      min: 0,
      max: 2,
    },
  };
  topMaskOptions: PredefinedInputMaskOptions = {
    numberFormat: {
      format: 'number',
      precision: 1,
      placeholder: '1.0',
      min: 0,
      max: 1,
    },
  };

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    modelName: new FormControl(''),
    systemInstruction: new FormControl(''),
    top_p: new FormControl(1),
    temperature: new FormControl(1),
  });

  get outputParameters(): OutputProperty[] {
    return this.function?.outputSchema || [];
  }

  constructor() {
    effect(() => {
      this.isValid.emit(this.isFormValid() && this.isChildValid());
    });
  }

  ngAfterViewInit(): void {
    // Initialize the form with the function data
    const newValue = parseFunctionToFormValue(this.function);
    if (!newValue.name) {
      // If the function is empty, initialize with default values
      this.functionWrapped.set(false);
    }

    // Extract outputSchema and patch only the top-level form fields
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { outputSchema, ...formValues } = newValue;
    this.form.patchValue(formValues);

    this.isFormValid.set(this.form.valid);
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = parseFunctionFormValue({
        ...value,
        outputSchema: this.outputParameters,
      } as OpenAiCustomFunctionForm);
      this.functionChange.emit(newValue);
      this.isFormValid.set(this.form.valid);
    });
    this.showAdvanced = this.form.value.top_p !== 1 || this.form.value.temperature !== 1;
    if (this.form.get('name')?.value?.length === 0) {
      this.functionNameInput?.nativeElement?.focus();
    }
  }

  updateParameters(value: OutputProperty[]): void {
    this.function = { ...this.function, outputSchema: value };
    this.functionChange.emit(this.function);
  }
}
